import { Box, Container, Link, Typography } from '@mui/material';
import { ErrorResponse } from '@remix-run/router';
import React, { Fragment, useEffect, useState } from 'react';
import { Link as RouterLink, isRouteErrorResponse, useRouteError } from 'react-router-dom';
import AppBar from '../app-bar/AppBar';
import ksimCaptain from '../assets/ksim-captain-unhappy.png';
import ksimCog from '../assets/ksim-cog.png';
import ksimMachinist from '../assets/ksim-machinist.png';
import tiles from '../assets/tiles-default.svg';
import { ProblemDetails } from '../types';

const Ksim = ({ status }: { status?: number }) => {
  const ksim =
    (status &&
      {
        403: ksimCog,
        404: ksimCaptain,
        500: ksimMachinist,
      }[status]) ||
    ksimCaptain;

  const size = (status &&
    {
      403: { width: 89, height: 319, right: 310, bottom: 400 },
      404: { width: 212, height: 400, right: 260, bottom: 350 },
      500: { width: 209, height: 287, right: 310, bottom: 400 },
    }[status]) || { width: 212, height: 400, right: 260, bottom: 350 };

  return (
    <Box
      alt="K-Sim"
      component="img"
      src={ksim}
      sx={{
        display: { xs: 'none', sm: 'inline' },
        position: 'absolute',
        ...size,
      }}
    />
  );
};

/**
 * Component to render an error page. There are some supported scenarios
 * - Response thrown after fetching data. We have to unwrap the JSON body manually
 * - ErrorResponse thrown in a React Router loader. It contains unwrapped data
 * - Any other Error
 */
const CatchBoundary = () => {
  const routeError = useRouteError() as ErrorResponse | Error;
  // Unwrap non-route error Reponses
  const [error, setError] = useState<ErrorResponse>();
  useEffect(() => {
    if (isRouteErrorResponse(routeError)) {
      setError(routeError);
    } else if (routeError instanceof Response) {
      const isJson =
        routeError.headers.get('Content-Type')?.includes('application/json') ||
        routeError.headers.get('Content-Type')?.includes('application/problem+json');
      (isJson ? routeError.clone().json() : routeError.clone().text()).then(data => {
        setError(new ErrorResponse(routeError.status, routeError.statusText, data));
      });
    }
  }, [routeError]);

  const data: ProblemDetails | undefined = error?.data;

  const heading = isRouteErrorResponse(error) ? error.statusText : 'Something went wrong';
  const subheading = isRouteErrorResponse(error) ? 'Error ' + error.status : 'Oops';

  const message = isRouteErrorResponse(error)
    ? data?.title ??
      {
        401: (
          <>
            You must be authenticated to view this page.
            <br />
            Go to back{' '}
            <Link component={RouterLink} to="/">
              home
            </Link>
            .
          </>
        ),
        403: (
          <>
            You don&apos;t have permission to view this page.
            <br />
            Go to back{' '}
            <Link component={RouterLink} to="/">
              home
            </Link>
            .
          </>
        ),
        404: (
          <>
            Sorry, the page could not be found or no longer exists.
            <br />
            Go to our homepage or feel free to{' '}
            <Link href="mailto:connect.service@kongsbergdigital.com">contact us</Link> if the problem persists.
          </>
        ),
        500: (
          <>
            Oops something went wrong.
            <br />
            Try to refresh this page or feel free to{' '}
            <Link href="mailto:connect.service@kongsbergdigital.com">contact us</Link> if the problem persists.
          </>
        ),
      }[error.status] ??
      'Oops something went wrong.'
    : 'Check Developer Tools (F12) for more details.';

  // Process errors (Problem details on Response, or Exceptions)
  const plainError = routeError as Error;

  const errorDetails =
    routeError instanceof Response || routeError instanceof ErrorResponse
      ? data?.errors && Object.entries(data.errors)
      : [[plainError.name, plainError.message]];

  return (
    <>
      <AppBar title="Error" />
      <Box
        sx={{
          background: { sm: `url(${tiles}) no-repeat right -280px bottom / 1276px 618px` },
          minHeight: 'calc(100vh -  48px)',
        }}
      >
        <Ksim status={error?.status} />
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            pt: { xs: 4, sm: 18 },
            pb: 4,
          }}
        >
          <Typography component="h2" variant="h5" color="text.secondary" gutterBottom>
            {subheading}
          </Typography>
          <Typography component="h1" variant="h3" mb={6}>
            {heading}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {message}
          </Typography>
          {errorDetails && (
            <Box component="dl" sx={{ color: 'text.secondary', mt: 4 }}>
              {errorDetails.map(([fieldName, errorMessage]) => (
                <Fragment key={fieldName}>
                  <dt>{fieldName}</dt>
                  <dd>{errorMessage}</dd>
                </Fragment>
              ))}
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default CatchBoundary;
